import { CALL_HISTORY_METHOD } from "connected-react-router";
import { CommonAppActionTypes } from "@konicaminolta/common-react";
import { AxiosError } from "axios";
import { Dashboard } from "./Dashboard/Dashboard";
import { Report } from "./Report/Report";
import { Group } from "./Group/Group";
import { DatasetRefreshHistory } from "./DatasetRefreshHistory/DatasetRefreshHistory";
import { EmbeddedRecordAccess } from "./Common/EmbeddedRecordAccess";
import { ReportAlias } from "./ReportAlias/ReportAlias";
import { DashboardAlias } from "./DashboardAlias/DashboardAlias";
import { Embed } from "powerbi-client";

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.

export const REHYDRATE = "persist/REHYDRATE";

export interface RehydrateAction {
    type: typeof REHYDRATE;
    payload: any;
}

export interface ReceivedError {
    error: AxiosError | Error;
    message?: string;
}

export interface ReceivedErrorAction {
    type: "error/receiveAxiosError";
    payload: ReceivedError;
}

//#region History
export interface CallHistoryMethod {
    type: typeof CALL_HISTORY_METHOD,
    payload: {
      method: string,
      args: any[]
    }
}
//#endregion

//#region Aggregate of all actions

export type AppActions = 
    CommonAppActionTypes | 
    GroupActionTypes |
    DatasetRefreshHistoryActionTypes |
    ReportActionTypes |
    ReportAliasActionTypes;

//#endregion

//#region Reports
export const REQUEST_REPORTS = "REQUEST_REPORTS";
export const RECEIVE_REPORTS = "RECEIVE_REPORTS";
export const REQUEST_REPORT_DETAILS = "REQUEST_REPORT_DETAILS";
export const RECEIVE_REPORT_DETAILS = "RECEIVE_REPORT_DETAILS";
export const UNLOAD_REPORT_DETAILS = "UNLOAD_REPORT_DETAILS";
export const REQUEST_REPORT_EMBEDDED_VIEW = "REQUEST_REPORT_EMBEDDED_VIEW";
export const RECEIVE_REPORT_EMBEDDED_VIEW = "RECEIVE_REPORT_EMBEDDED_VIEW";
export const UNLOAD_REPORT_EMBEDDED_VIEW = "UNLOAD_REPORT_EMBEDDED_VIEW";
export const SET_REPORT_EMBEDDED_COMPONENT = "SET_REPORT_EMBEDDED_COMPONENT";
export const UNLOAD_REPORT_EMBEDDED_COMPONENT = "UNLOAD_REPORT_EMBEDDED_COMPONENT";


export interface RequestReportsAction {
    type: typeof REQUEST_REPORTS;
}
export interface ReceiveReportsAction {
    type: typeof RECEIVE_REPORTS;
    data: Report[];
    hasPermissionsToList: boolean;
}
export interface RequestReportDetailsAction {
    type: typeof REQUEST_REPORT_DETAILS;
}
export interface ReceiveReportDetailsAction {
    type: typeof RECEIVE_REPORT_DETAILS;
    report?: Report;
    hasPermissionsToDetails?: boolean;
}
export interface UnloadReportDetailsAction {
    type: typeof UNLOAD_REPORT_DETAILS;
}
export interface RequestReportEmbeddedViewAction {
    type: typeof REQUEST_REPORT_EMBEDDED_VIEW;
}
export interface ReceiveReportEmbeddedViewAction {
    type: typeof RECEIVE_REPORT_EMBEDDED_VIEW;
    data?: EmbeddedRecordAccess;
}
export interface UnloadReportEmbeddedViewAction {
    type: typeof UNLOAD_REPORT_EMBEDDED_VIEW;
}

export interface SetReportEmbeddedComponent {
    type: typeof SET_REPORT_EMBEDDED_COMPONENT;
    powerBiEmbeddedReport: Embed;
}

export interface UnloadReportEmbeddedComponent {
    type: typeof UNLOAD_REPORT_EMBEDDED_COMPONENT;
}

export type ReportActionTypes =
    ReceivedErrorAction |
    CallHistoryMethod |
    RequestReportsAction |
    ReceiveReportsAction | 
    RequestReportDetailsAction |
    ReceiveReportDetailsAction | 
    UnloadReportDetailsAction |
    RequestReportEmbeddedViewAction |
    ReceiveReportEmbeddedViewAction |
    UnloadReportEmbeddedViewAction | 
    SetReportEmbeddedComponent | 
    UnloadReportEmbeddedComponent;

//#endregion

//#region Report Aliases

export const REQUEST_REPORT_ALIASES = "REQUEST_REPORT_ALIASES";
export const RECEIVE_REPORT_ALIASES = "RECEIVE_REPORT_ALIASES";
export const REQUEST_CREATE_REPORT_ALIAS = "REQUEST_CREATE_REPORT_ALIAS";
export const RECEIVE_CREATE_REPORT_ALIAS = "RECEIVE_CREATE_REPORT_ALIAS";
export const RECEIVE_CREATE_REPORT_ALIAS_ERROR = "RECEIVE_CREATE_REPORT_ALIAS_ERROR";

export interface RequestReportAliasesAction {
    type: typeof REQUEST_REPORT_ALIASES;
}

export interface ReceiveReportAliasesAction {
    type: typeof RECEIVE_REPORT_ALIASES;
    reportAliases: ReportAlias[];
}

export interface RequestCreateReportAliasAction {
    type: typeof REQUEST_CREATE_REPORT_ALIAS;
}

export interface ReceiveCreateReportAliasAction {
    type: typeof RECEIVE_CREATE_REPORT_ALIAS;
    reportAlias: ReportAlias;
}
export interface ReceiveCreateReportAliasErrorAction {
    type: typeof RECEIVE_CREATE_REPORT_ALIAS_ERROR;
    message: string;
}

export type ReportAliasActionTypes =
    ReceivedErrorAction |
    RequestReportAliasesAction |
    ReceiveReportAliasesAction |
    ReceiveReportAliasesAction |
    RequestCreateReportAliasAction |
    ReceiveCreateReportAliasAction |
    ReceiveCreateReportAliasErrorAction;


//#endregion

//#region Groups
export const REQUEST_GROUPS = "REQUEST_GROUPS";
export const RECEIVE_GROUPS = "RECEIVE_GROUPS";
export const REQUEST_GROUP_DETAILS = "REQUEST_GROUP_DETAILS";
export const RECEIVE_GROUP_DETAILS = "RECEIVE_GROUP_DETAILS";
export const UNLOAD_GROUP_DETAILS = "UNLOAD_GROUP_DETAILS";

export interface RequestGroupsAction {
    type: typeof REQUEST_GROUPS;
}
export interface ReceiveGroupsAction {
    type: typeof RECEIVE_GROUPS;
    data: Group[];
}
export interface RequestGroupDetailsAction {
    type: typeof REQUEST_GROUP_DETAILS;
}
export interface ReceiveGroupDetailsAction {
    type: typeof RECEIVE_GROUP_DETAILS;
    group?: Group;
}
export interface UnloadGroupDetailsAction {
    type: typeof UNLOAD_GROUP_DETAILS;
}

export type GroupActionTypes =
    ReceivedErrorAction |
    CallHistoryMethod |
    RequestGroupsAction |
    ReceiveGroupsAction | 
    RequestGroupDetailsAction |
    ReceiveGroupDetailsAction | 
    UnloadGroupDetailsAction; 

//#endregion

//region DatasetRefreshHistory
export const REQUEST_DATASET_REFRESH_HISTORY = "REQUEST_DATASET_REFRESH_HISTORY";
export const RECEIVE_DATASET_REFRESH_HISTORY = "RECEIVE_DATASET_REFRESH_HISTORY";
export const UNLOAD_DATASET_REFRESH_HISTORY = "UNLOAD_DATASET_REFRESH_HISTORY";

export interface RequestDatasetRefreshHistoryAction {
    type: typeof REQUEST_DATASET_REFRESH_HISTORY;
}
export interface ReceiveDatasetRefreshHistoryAction {
    type: typeof RECEIVE_DATASET_REFRESH_HISTORY;
    data: DatasetRefreshHistory[];
}
export interface UploadDatasetRefreshHistoryAction {
    type: typeof UNLOAD_DATASET_REFRESH_HISTORY;
}

export type DatasetRefreshHistoryActionTypes = 
    ReceivedErrorAction |
    CallHistoryMethod |
    RequestDatasetRefreshHistoryAction |
    ReceiveDatasetRefreshHistoryAction |
    UploadDatasetRefreshHistoryAction;

//#endregion

//#region Dashboards
export const REQUEST_DASHBOARDS = "REQUEST_DASHBOARDS";
export const RECEIVE_DASHBOARDS = "RECEIVE_DASHBOARDS";
export const REQUEST_DASHBOARD_DETAILS = "REQUEST_DASHBOARD_DETAILS";
export const RECEIVE_DASHBOARD_DETAILS = "RECEIVE_DASHBOARD_DETAILS";
export const UNLOAD_DASHBOARD_DETAILS = "UNLOAD_DASHBOARD_DETAILS";
export const REQUEST_DASHBOARD_EMBEDDED_VIEW = "REQUEST_DASHBOARD_EMBEDDED_VIEW";
export const RECEIVE_DASHBOARD_EMBEDDED_VIEW = "RECEIVE_DASHBOARD_EMBEDDED_VIEW";
export const UNLOAD_DASHBOARD_EMBEDDED_VIEW = "UNLOAD_DASHBOARD_EMBEDDED_VIEW";


export interface RequestDashboardsAction {
    type: typeof REQUEST_DASHBOARDS;
}
export interface ReceiveDashboardsAction {
    type: typeof RECEIVE_DASHBOARDS;
    data: Dashboard[];
    hasPermissionsToList: boolean;
}
export interface RequestDashboardDetailsAction {
    type: typeof REQUEST_DASHBOARD_DETAILS;
}
export interface ReceiveDashboardDetailsAction {
    type: typeof RECEIVE_DASHBOARD_DETAILS;
    dashboard?: Dashboard;
    hasPermissionsToDetails: boolean;
}
export interface UnloadDashboardDetailsAction {
    type: typeof UNLOAD_DASHBOARD_DETAILS;
}
export interface RequestDashboardEmbeddedViewAction {
    type: typeof REQUEST_DASHBOARD_EMBEDDED_VIEW;
}
export interface ReceiveDashboardEmbeddedViewAction {
    type: typeof RECEIVE_DASHBOARD_EMBEDDED_VIEW;
    data?: EmbeddedRecordAccess;
}
export interface UnloadDashboardEmbeddedViewAction {
    type: typeof UNLOAD_DASHBOARD_EMBEDDED_VIEW;
}


export type DashboardActionTypes =
    ReceivedErrorAction |
    CallHistoryMethod |
    RequestDashboardsAction |
    ReceiveDashboardsAction | 
    RequestDashboardDetailsAction |
    ReceiveDashboardDetailsAction | 
    UnloadDashboardDetailsAction |
    RequestDashboardEmbeddedViewAction |
    ReceiveDashboardEmbeddedViewAction |
    UnloadDashboardEmbeddedViewAction;

//#endregion

//#region Dashboard Aliases

export const REQUEST_DASHBOARD_ALIASES = "REQUEST_DASHBOARD_ALIASES";
export const RECEIVE_DASHBOARD_ALIASES = "RECEIVE_DASHBOARD_ALIASES";
export const REQUEST_CREATE_DASHBOARD_ALIAS = "REQUEST_CREATE_DASHBOARD_ALIAS";
export const RECEIVE_CREATE_DASHBOARD_ALIAS = "RECEIVE_CREATE_DASHBOARD_ALIAS";
export const RECEIVE_CREATE_DASHBOARD_ALIAS_ERROR = "RECEIVE_CREATE_DASHBOARD_ALIAS_ERROR";

export interface RequestDashboardAliasesAction {
    type: typeof REQUEST_DASHBOARD_ALIASES;
}

export interface ReceiveDashboardAliasesAction {
    type: typeof RECEIVE_DASHBOARD_ALIASES;
    dashboardAliases: DashboardAlias[];
}

export interface RequestCreateDashboardAliasAction {
    type: typeof REQUEST_CREATE_DASHBOARD_ALIAS;
}

export interface ReceiveCreateDashboardAliasAction {
    type: typeof RECEIVE_CREATE_DASHBOARD_ALIAS;
    dashboardAlias: DashboardAlias;
}
export interface ReceiveCreateDashboardAliasErrorAction {
    type: typeof RECEIVE_CREATE_DASHBOARD_ALIAS_ERROR;
    message: string;
}

export type DashboardAliasActionTypes =
    ReceivedErrorAction |
    RequestDashboardAliasesAction |
    ReceiveDashboardAliasesAction |
    ReceiveDashboardAliasesAction |
    RequestCreateDashboardAliasAction |
    ReceiveCreateDashboardAliasAction |
    ReceiveCreateDashboardAliasErrorAction;


//#endregion